import { infiniteQueryOptions, useInfiniteQuery } from '@tanstack/react-query'

import type { TCommonMetaProps, TFields, TMeta } from '@/src/general/utilities/fetcher/api.types'

import { communityApi, SUGGESTION_LIMIT } from '../api/communityApi'
import type {
  TGetCommunitiesApiOptionalFields,
  TGetCommunitiesApiPayload,
  TGetCommunitiesApiPayloadFilter,
} from '../api/getCommunities.types'
import { COMMUNITIES_CATEGORY, COMMUNITY_FILTER } from '../models/community.constants'

export const getCommunitiesCategoryQueryParams = (tag: string | COMMUNITIES_CATEGORY) => {
  const commonParams = {
    limit: SUGGESTION_LIMIT,
    fields: ['lastPublication', 'hasJoined', 'sharesCount'],
    meta: ['globalCount', 'hasMore'],
  } as const satisfies TFields<TGetCommunitiesApiOptionalFields> &
    TMeta<TCommonMetaProps> &
    Pick<TGetCommunitiesApiPayload, 'limit'>

  let dedicatedParams: Pick<TGetCommunitiesApiPayload, 'isFeatured' | 'ignoreWizard' | 'isFavorite' | 'isSeasonal'> &
    TGetCommunitiesApiPayloadFilter & { enabled?: boolean }

  // oxlint-disable @typescript-eslint/no-unsafe-enum-comparison
  switch (tag) {
    case COMMUNITIES_CATEGORY.SUGGESTED:
      dedicatedParams = {}
      break
    case COMMUNITIES_CATEGORY.CURRENTLY:
      dedicatedParams = {
        isFeatured: false,
        ignoreWizard: true,
      }
      break
    case COMMUNITIES_CATEGORY.TEAM_FAVORITES:
      dedicatedParams = {
        isFavorite: true,
        isSeasonal: false,
      }
      break
    default:
      dedicatedParams = {
        filter: COMMUNITY_FILTER.TAG,
        filterParam: tag,
        enabled: tag !== '',
      }
      break
  }
  // oxlint-enable @typescript-eslint/no-unsafe-enum-comparison

  return { ...commonParams, ...dedicatedParams }
}

type TQueryOptions = { enabled?: boolean }
export const getCommunitiesOptions = <
  Fields extends TFields<TGetCommunitiesApiOptionalFields>,
  Meta extends TMeta<TCommonMetaProps>,
>(
  params: TGetCommunitiesApiPayload & Fields & Meta & TQueryOptions,
  tag?: string,
) => {
  const { enabled, ...restParams } = params
  return infiniteQueryOptions({
    // oxlint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: ['get_communities', params],
    queryFn: ({ signal, pageParam: queryParam }) =>
      communityApi.getCommunities(
        {
          ...restParams,
          ...queryParam,
        },
        { signal },
        tag,
      ),
    getNextPageParam: (lastPage, _pages, { limit, offset }) =>
      lastPage.length === limit ? { offset: offset + limit, limit } : null,
    initialPageParam: { offset: 0, limit: params?.limit || 3 },
    retry: 0,
    staleTime: 60 * 1000 * 10, // 10 minutes
    select: (data) =>
      data.pages.reduce<(typeof data.pages)[number][number][]>((acc, page) => {
        acc.push(...page)
        return acc
      }, []),
    enabled: enabled !== undefined ? enabled : true,
  })
}
export const useCommunitiesQuery = <
  Fields extends TFields<TGetCommunitiesApiOptionalFields>,
  Meta extends TMeta<TCommonMetaProps>,
>(
  params: TGetCommunitiesApiPayload & Fields & Meta & TQueryOptions,
  tag?: string,
) => useInfiniteQuery(getCommunitiesOptions(params, tag))
